// Progress-bar logic for the merged Gateways stage (reveal-gateways-onetab follow-up).
//
// HONEST PROGRESS, RECONCILED. The reveal's load-bearing invariant (revealStaging
// task 7.1) is "never a timed fake spinner" — progress must reflect real state, and
// nothing silently claims completion. These two bars are compatible with that and
// read as ONE continuous progression across the two beats:
//
//   - The SEARCH bar is PHASE-AWARE (not a pure timer): it advances through the
//     real discovery phases (scan_scope → gateway_scan → connection_validation) in
//     bands, easing within each band over time. A long phase parks at that band's
//     ceiling (e.g. connection_validation at {@link SCAN_HANDOFF} ≈ 75%) — honest
//     "more to go" mid-progress, NOT a bar parked near-done. It only mounts while
//     the scan is genuinely in flight (`!scanSettled`); it reaches 1 only when the
//     real scan settles.
//   - The DETERMINING bar CONTINUES from {@link SCAN_HANDOFF} (the scan's hand-off)
//     toward {@link HONEST_PROGRESS_CAP}, so it never jumps backward when the scan
//     beat swaps to the decision beat. It is capped below 100% until the analysis
//     is actually `ready`, at which point the panel renders the pick.
//
// Pure (no React/DOM/timers) so the Node-only web test runner covers it directly.

import { SETUP_PHASES, phaseIndex, type AnalysisReadiness } from './revealStaging';

/**
 * The most a determinate bar driven by an ESTIMATE (not a real terminus) may ever
 * fill. The determining bar eases toward this; only `analysis === 'ready'` completes
 * it to 1.0, so it can never claim completion the data hasn't earned.
 */
export const HONEST_PROGRESS_CAP = 0.92;

/**
 * The hand-off fraction between the two bars: the scan bar's top (connection_validation
 * band ceiling) AND the determining bar's floor. Keeping them equal makes the scan→
 * determine swap read as one continuous bar with no backward jump.
 */
export const SCAN_HANDOFF = 0.75;

/** Per-phase ease time for the search bar's within-band fill (tunable). */
export const SCAN_PHASE_ESTIMATE_MS = 8_000;

// Search-bar bands: each discovery phase fills a slice of [0, SCAN_HANDOFF]. The
// terminus phase `device_inventory` is the SETTLE point (the beat swaps to the
// determining bar), so it is not a band here.
const SCAN_PHASE_BANDS: ReadonlyArray<readonly [string, number, number]> = [
  ['scan_scope', 0.0, 0.2],
  ['gateway_scan', 0.2, 0.5],
  ['connection_validation', 0.5, SCAN_HANDOFF],
];

/**
 * Phase-aware search fill in [0, 1]. Within the current phase's band it eases over
 * `elapsedInPhaseMs` toward (never past) the band ceiling, so a long phase parks at
 * an honest mid-progress position rather than near-done. Returns exactly 1 only when
 * `settled`. Clamps NaN / non-positive elapsed to the band base.
 */
export function scanProgressFraction(
  phase: string | null,
  elapsedInPhaseMs: number,
  settled: boolean,
  estimateMs: number = SCAN_PHASE_ESTIMATE_MS
): number {
  if (settled) return 1;
  const band = SCAN_PHASE_BANDS.find(([p]) => p === phase);
  if (!band) {
    // Unknown / pre-snapshot phase → the very start. A discovery-arc phase past
    // our last band (shouldn't happen pre-settle) → the hand-off ceiling.
    return phaseIndex(phase) >= phaseIndex('connection_validation') ? SCAN_HANDOFF : 0;
  }
  const [, base, ceiling] = band;
  if (!(elapsedInPhaseMs > 0) || !(estimateMs > 0)) return base;
  const span = ceiling - base;
  const k = 3; // ~1 - e^-3 ≈ 0.95 of the band span at t = estimate
  return Math.min(ceiling, base + span * (1 - Math.exp((-k * elapsedInPhaseMs) / estimateMs)));
}

/** Honest, phase-aware stage text under the search bar. */
export function scanStageDescription(phase: string | null): string {
  switch (phase) {
    case 'scan_scope':
      return 'Mapping your network…';
    case 'gateway_scan':
      return 'Listening for gateways…';
    case 'connection_validation':
      return 'Checking each connection…';
    default:
      return 'Scanning your network…';
  }
}

/**
 * The discrete, ordered steps of the priority-selection (determining) beat. The
 * analysis pipeline emits NO intermediate progress (only none→loading→ready), so
 * these are SYNTHESIZED from coarse real signals; the labels double as the stage
 * description under the step bar.
 */
export const DETERMINING_STEPS = [
  'Collecting device data',
  'Running the analysis',
  'Choosing your main connection',
] as const;

export interface DeterminingSignals {
  /** Source-analysis lane readiness. */
  analysis: AnalysisReadiness;
  /** Devices catalogued so far (useBoatDevices), arriving concurrently. */
  deviceCount: number;
  /** `phaseIndex(session.phase)` from revealStaging (-1 when unknown). */
  phaseIndex: number;
}

export interface DeterminingProgress {
  currentStep: number;
  totalSteps: number;
  /** The current step's label (also the stage-description text). */
  label: string;
  /** Bar fill in [0, 1]; continues from {@link SCAN_HANDOFF}; 1 only once `ready`. */
  fraction: number;
}

/**
 * Step-based determining progress, synthesized from coarse real signals:
 *   1. "Collecting device data"        — analysis kicked (none-pre-kick / loading)
 *   2. "Running the analysis"          — device data has started arriving
 *   3. "Choosing your main connection" — the snapshot phase reached source_analysis
 * `analysis === 'ready'` jumps to the final step at fraction 1 (the caller then
 * renders the recommendation). `floorStep` is the component's high-water mark — it
 * keeps the step monotonic even if a signal momentarily regresses. The fraction is
 * mapped into [SCAN_HANDOFF, HONEST_PROGRESS_CAP] so the bar CONTINUES the scan bar
 * (no backward jump) and stays below 100% until `ready`.
 */
export function determiningProgress(s: DeterminingSignals, floorStep = 0): DeterminingProgress {
  const totalSteps = DETERMINING_STEPS.length;
  const sourceAnalysisIdx = SETUP_PHASES.indexOf('source_analysis');
  let step = 1;
  if (s.deviceCount > 0) step = 2;
  if (sourceAnalysisIdx >= 0 && s.phaseIndex >= sourceAnalysisIdx) step = 3;
  if (s.analysis === 'ready') step = totalSteps;
  step = Math.max(step, floorStep, 1);
  const ready = s.analysis === 'ready';
  const fraction = ready
    ? 1
    : SCAN_HANDOFF + (HONEST_PROGRESS_CAP - SCAN_HANDOFF) * (step / totalSteps);
  return { currentStep: step, totalSteps, label: DETERMINING_STEPS[step - 1]!, fraction };
}
